import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  ReferenceLine
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Activity, AlertTriangle, 
  RefreshCw, Calendar, DollarSign, Zap, Target, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getDemoForecastData = () => {
  const today = new Date();
  const historical = [];
  const daily_forecast = [];
  
  for (let i = 30; i > 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    historical.push({
      date: d.toISOString().split('T')[0],
      cost: 8500 + Math.sin(i * 0.3) * 2000 + (30 - i) * 50,
    });
  }
  
  for (let i = 0; i < 30; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    const base = 9800 + i * 30;
    daily_forecast.push({
      date: d.toISOString().split('T')[0],
      predicted_cost: base,
      lower_bound: base * 0.85,
      upper_bound: base * 1.15,
    });
  }
  
  return {
    total_current_monthly: 284500,
    total_forecasted_monthly: 298200,
    total_change_percentage: 4.8,
    providers: [{
      provider: 'All Providers',
      current_monthly: 284500,
      forecasted_monthly: 298200,
      change_percentage: 4.8,
      confidence: 0.89,
      historical,
      daily_forecast,
      services: [
        { service: 'EC2 Compute', current: 95200, forecasted: 101800, change: 6.9 },
        { service: 'S3 Storage', current: 42300, forecasted: 43100, change: 1.9 },
        { service: 'RDS Database', current: 38700, forecasted: 39400, change: 1.8 },
        { service: 'Lambda', current: 18400, forecasted: 19800, change: 7.6 },
        { service: 'CloudFront', current: 12600, forecasted: 13200, change: 4.8 },
      ]
    }],
    generated_at: new Date().toISOString()
  };
};

const CostForecasting = () => {
  const { shouldShowData } = useDemoData();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [forecastData, setForecastData] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [forecastDays, setForecastDays] = useState(30);

  const fetchForecast = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/forecasting/predict`,
        { provider: selectedProvider, days: forecastDays, include_services: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data;
      if ((!data.providers || data.providers.length === 0) && data.total_current_monthly === 0 && shouldShowData) {
        setForecastData(getDemoForecastData());
      } else {
        setForecastData(data);
      }
    } catch (error) {
      console.error('Failed to fetch forecast:', error);
      if (shouldShowData) {
        setForecastData(getDemoForecastData());
      } else {
        toast.error('Failed to load forecast data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchForecast();
  }, [selectedProvider, forecastDays]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchForecast();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Show empty state when no data is available
  if (!forecastData || (forecastData.providers && forecastData.providers.length === 0)) {
    if (!shouldShowData) {
    return (
      <div className="p-6 space-y-6" data-testid="cost-forecasting-empty">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-emerald-500" />
            Cost Forecasting
          </h1>
          <p className="text-slate-400 dark:text-slate-400">
            AI-powered predictions for your cloud spending
          </p>
        </div>
        <Card className="border-dashed border-2 border-slate-300 dark:border-slate-600">
          <CardContent className="py-16 text-center">
            <TrendingUp className="w-16 h-16 mx-auto text-slate-400 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
              No Forecast Data Available
            </h3>
            <p className="text-sm text-slate-400 dark:text-slate-400 mb-6 max-w-md mx-auto">
              Connect your cloud providers to see AI-powered cost forecasting and predictions.
            </p>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => window.location.href = '/cloud-audit'}
              data-testid="connect-for-forecast-btn"
            >
              Connect Cloud Account
            </Button>
          </CardContent>
        </Card>
      </div>
    );
    }
  }

  const isMultiProvider = selectedProvider === 'all';
  const providers = isMultiProvider ? forecastData?.providers : [forecastData];

  // Prepare chart data
  const getChartData = () => {
    if (!forecastData) return [];
    
    if (isMultiProvider && forecastData.providers) {
      // Combine all provider data for multi-provider view
      const provider = forecastData.providers[0];
      if (!provider) return [];
      
      const historical = provider.historical || [];
      const forecast = provider.daily_forecast || [];
      
      return [
        ...historical.map(d => ({ ...d, type: 'historical' })),
        ...forecast.map(d => ({ ...d, type: 'forecast' }))
      ];
    }
    
    const historical = forecastData?.historical || [];
    const forecast = forecastData?.daily_forecast || [];
    
    return [
      ...historical.map(d => ({ ...d, type: 'historical' })),
      ...forecast.map(d => ({ ...d, type: 'forecast' }))
    ];
  };

  const chartData = getChartData();

  return (
    <div className="p-6 space-y-6" data-testid="cost-forecasting">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-emerald-500" />
            Cost Forecasting
          </h1>
          <p className="text-slate-400 dark:text-slate-400">
            AI-powered predictions for your cloud spending
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value)}
            className="px-3 py-2 border rounded-lg bg-white dark:bg-slate-800"
          >
            <option value="all">All Providers</option>
            <option value="aws">AWS</option>
            <option value="azure">Azure</option>
            <option value="gcp">GCP</option>
            <option value="snowflake">Snowflake</option>
          </select>
          <select
            value={forecastDays}
            onChange={(e) => setForecastDays(Number(e.target.value))}
            className="px-3 py-2 border rounded-lg bg-white dark:bg-slate-800"
          >
            <option value={14}>14 Days</option>
            <option value={30}>30 Days</option>
            <option value={60}>60 Days</option>
            <option value={90}>90 Days</option>
          </select>
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800 bg-gradient-to-br from-slate-800 to-slate-900">
          <CardContent className="pt-6">
            <div className="text-slate-400 text-sm mb-1">Current Monthly</div>
            <div className="text-2xl font-bold text-white">
              {formatCurrency(isMultiProvider ? forecastData?.total_current_monthly : forecastData?.current_monthly_cost)}
            </div>
            <div className="text-slate-400 text-xs mt-1">Last 30 days</div>
          </CardContent>
        </Card>

        <Card className="bg-emerald-600 bg-gradient-to-br from-emerald-600 to-emerald-700">
          <CardContent className="pt-6">
            <div className="text-emerald-100 text-sm mb-1">Forecasted Monthly</div>
            <div className="text-2xl font-bold text-white">
              {formatCurrency(isMultiProvider ? forecastData?.total_forecasted_monthly : forecastData?.forecasted_monthly_cost)}
            </div>
            <div className="text-emerald-200 text-xs mt-1">Next {forecastDays} days</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-slate-400 text-sm mb-1">Projected Change</div>
            <div className={`text-2xl font-bold flex items-center gap-2 ${
              (isMultiProvider ? forecastData?.total_change_percentage : forecastData?.change_percentage) > 0 
                ? 'text-red-500' : 'text-emerald-500'
            }`}>
              {(isMultiProvider ? forecastData?.total_change_percentage : forecastData?.change_percentage) > 0 
                ? <TrendingUp className="w-5 h-5" /> 
                : <TrendingDown className="w-5 h-5" />
              }
              {Math.abs(isMultiProvider ? forecastData?.total_change_percentage : forecastData?.change_percentage)}%
            </div>
            <div className="text-slate-400 text-xs mt-1">vs. current period</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-slate-400 text-sm mb-1">Confidence</div>
            <div className="text-2xl font-bold text-emerald-500">
              {((forecastData?.confidence || 0.85) * 100).toFixed(0)}%
            </div>
            <div className="text-slate-400 text-xs mt-1">Prediction accuracy</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-emerald-500" />
            Cost Trend & Forecast
          </CardTitle>
          <CardDescription>Historical data and AI predictions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="historicalGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#059669" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  stroke="#6B7280"
                  tick={{ fill: '#6B7280', fontSize: 11 }}
                />
                <YAxis 
                  stroke="#6B7280"
                  tick={{ fill: '#6B7280', fontSize: 11 }}
                  tickFormatter={(v) => `$${v}`}
                />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }}
                  labelStyle={{ color: '#9CA3AF' }}
                  formatter={(value, name) => [`$${value.toFixed(2)}`, name]}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="cost"
                  name="Actual Cost"
                  stroke="#059669"
                  fill="url(#historicalGradient)"
                  strokeWidth={2}
                  dot={false}
                />
                {chartData.some(d => d.is_forecast) && (
                  <>
                    <Area
                      type="monotone"
                      dataKey="upper_bound"
                      name="Upper Bound"
                      stroke="#8B5CF6"
                      fill="url(#forecastGradient)"
                      strokeDasharray="5 5"
                      strokeWidth={1}
                      dot={false}
                    />
                    <Area
                      type="monotone"
                      dataKey="lower_bound"
                      name="Lower Bound"
                      stroke="#8B5CF6"
                      fill="none"
                      strokeDasharray="5 5"
                      strokeWidth={1}
                      dot={false}
                    />
                  </>
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
              <span className="text-slate-400">Historical</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
              <span className="text-slate-400">Forecast Range</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Insights */}
      {providers && providers[0]?.insights && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              AI Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {providers[0].insights.map((insight, idx) => (
                <div 
                  key={idx}
                  className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                >
                  <p className="text-slate-700 dark:text-slate-300">{insight}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Service Breakdown */}
      {providers && providers[0]?.service_forecasts && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-500" />
              Service Forecast Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {providers[0].service_forecasts.map((service, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-8 rounded-full ${
                      service.trend === 'up' ? 'bg-red-500' : 
                      service.trend === 'down' ? 'bg-emerald-500' : 'bg-slate-400'
                    }`} />
                    <div>
                      <p className="font-medium text-slate-800 dark:text-white">{service.service}</p>
                      <p className="text-sm text-slate-400">{service.percentage}% of total</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-slate-800 dark:text-white">
                      {formatCurrency(service.forecasted_cost)}
                    </p>
                    <p className={`text-sm ${
                      service.trend === 'up' ? 'text-red-500' : 
                      service.trend === 'down' ? 'text-emerald-500' : 'text-slate-400'
                    }`}>
                      {service.trend === 'up' ? '↑' : service.trend === 'down' ? '↓' : '→'} 
                      {Math.abs(service.trend_value)}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CostForecasting;
